import { useState } from "react";
import { UserPlus } from "lucide-react";
import { apiFetch } from "../../data/session";

/**
 * F2 in the chat: the parent invites the child. Real call to
 * POST /api/family/students. In the simulator (no account token) it stays a
 * simulation and says so. With a known first name, only the email is asked.
 */
const InviteChildCard = ({ time, accountToken, onInvited, mobileType = false, childName = "", childEmail = "", memberId = "" }) => {
  const [name, setName] = useState(childName);
  const [email, setEmail] = useState(childEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const simulated = !accountToken;
  const knownName = Boolean(childName);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (name.trim().length < 2 || !email.includes("@")) {
      setError(knownName ? "Indiquez le courriel de votre enfant." : "Indiquez le prénom et le courriel de votre enfant.");
      return;
    }
    if (simulated) {
      const member = { name: name.trim(), email: email.trim(), simulated: true };
      setDone(member);
      onInvited?.(member);
      return;
    }
    setBusy(true);
    try {
      const member = memberId
        ? await apiFetch(`/api/family/students/${memberId}/email`, accountToken, {
            method: "POST",
            body: JSON.stringify({ email: email.trim() }),
          })
        : await apiFetch("/api/family/students", accountToken, {
            method: "POST",
            body: JSON.stringify({ name: name.trim(), email: email.trim() }),
          });
      setDone(member);
      onInvited?.(member);
    } catch (problem) {
      setError(problem.message || "Invitation impossible.");
    } finally {
      setBusy(false);
    }
  };

  const text = mobileType ? "text-[15px]" : "text-[13px]";
  const field = `mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 ${text} outline-none focus:border-[#25D366]`;
  const label = "block text-[11px] font-bold uppercase tracking-wider text-[#189346]";

  return (
    <div className="max-w-[85%] self-start rounded-2xl rounded-tl-sm bg-white p-3 shadow-sm" data-testid="invite-child-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E9FBF0] text-[#189346]">
          <UserPlus className="h-4 w-4" />
        </span>
        <p className={`font-semibold text-[#122118] ${text}`}>
          {memberId ? `Corriger le courriel de ${childName}` : knownName ? `Inviter ${childName}` : "Inviter mon enfant"}
        </p>
      </div>
      {done ? (
        <div className={`mt-3 break-words text-[#2d5a3d] ${text}`} data-testid="invite-child-done">
          <p>
            ✓ {done.name} · {done.email}
          </p>
          {done.simulated && (
            <p className="mt-1 text-xs text-[#9a6b00]">Simulation : aucun compte créé, aucun courriel envoyé.</p>
          )}
          {!done.simulated && !done.delivered && done.confirmPath && (
            <p className="mt-1 break-all text-xs text-[#6b5400]" data-testid="invite-child-devlink">
              Mode local, aucun courriel envoyé. Lien :{" "}
              <a className="underline" href={done.confirmPath}>
                {done.confirmPath}
              </a>
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-3">
          {!knownName && (
            <>
              <label className={label} htmlFor="invite-child-name">
                Prénom de votre enfant
              </label>
              <input
                id="invite-child-name"
                data-testid="invite-child-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className={`${field} mb-2`}
              />
            </>
          )}
          <label className={label} htmlFor="invite-child-email">
            Courriel de votre enfant
          </label>
          <input
            id="invite-child-email"
            data-testid="invite-child-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className={field}
          />
          {error && (
            <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="invite-child-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="invite-child-submit"
            disabled={busy}
            className={`mt-3 w-full rounded-full bg-[#25D366] px-4 py-2 font-semibold text-[#0a2914] disabled:opacity-50 ${text}`}
          >
            {busy ? "Envoi…" : memberId ? "Corriger et renvoyer" : "Envoyer l’invitation"}
          </button>
        </form>
      )}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default InviteChildCard;
